/**
 * Client-side DataDog configuration
 * This file contains browser-only DataDog functionality (RUM and Logs)
 * Safe to import in client components
 */

import { getEnvironmentVersion } from '../../../utils/version';

// Lazy load Datadog modules to prevent multiple imports during hot reloading
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let datadogRum: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let datadogLogs: any = null;

const loadDatadogModules = async () => {
	if (!datadogRum || !datadogLogs) {
		const [{ datadogRum: rum }, { datadogLogs: logs }] = await Promise.all([
			import('@datadog/browser-rum'),
			import('@datadog/browser-logs'),
		]);
		datadogRum = rum;
		datadogLogs = logs;
	}
	return { datadogRum, datadogLogs };
};

// Use a more robust initialization check
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isInitialized = () => {
	if (typeof window === 'undefined') return false;

	// Check multiple indicators that Datadog is already initialized
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const windowAny = window as any;

	// Check our custom flag
	if (windowAny.datadogInitialized) return true;

	// Check if Datadog RUM or Logs are already initialized
	for (const sdk of [windowAny.DD_RUM, windowAny.DD_LOGS]) {
		if (sdk && sdk.getInternalContext) {
			try {
				const context = sdk.getInternalContext();
				if (context && context.application_id) return true;
			} catch (e) {
				// Ignore errors, continue checking
			}
		}
	}

	// Check for Datadog global objects
	if (windowAny.DD_RUM || windowAny.DD_LOGS) return true;

	return false;
};

const markAsInitialized = () => {
	if (typeof window !== 'undefined') {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(window as any).datadogInitialized = true;
	}
};

// Helper function to safely initialize Datadog SDKs with consistent error handling
const safeInitialize = (sdkName: string, initFn: () => void) => {
	try {
		initFn();
	} catch (error) {
		// Silently ignore initialization errors during development
		if (process.env.NODE_ENV !== 'development') {
			console.warn(`Datadog ${sdkName} already initialized or failed to initialize:`, error);
		}
	}
};

export const initializeDatadog = async () => {
	const requiredEnvVars = {
		environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
		clientToken: process.env.NEXT_PUBLIC_MS_DATADOGRUM_TOKEN,
		applicationId: process.env.NEXT_PUBLIC_MS_DATADOGRUM_APP_ID,
	};

	const hasAllRequiredVars = Object.values(requiredEnvVars).every(Boolean);
	const alreadyInitialized = isInitialized();
	if (hasAllRequiredVars && !alreadyInitialized) {
		markAsInitialized();

		// Load Datadog modules
		const { datadogRum: rum, datadogLogs: logs } = await loadDatadogModules();

		// Get environment-specific version from package.json
		const environmentVersion = getEnvironmentVersion();

		// Initialize Datadog Logs
		safeInitialize('Logs', () => {
			logs.init({
				clientToken: requiredEnvVars.clientToken as string,
				site: 'datadoghq.com',
				service: 'praxis-next',
				version: environmentVersion,
				env: requiredEnvVars.environment as string,
				forwardConsoleLogs: 'all',
			});
		});

		// Initialize Datadog RUM
		safeInitialize('RUM', () => {
			rum.init({
				applicationId: requiredEnvVars.applicationId as string,
				clientToken: requiredEnvVars.clientToken as string,
				site: 'datadoghq.com',
				service: 'praxis-next',
				version: environmentVersion,
				env: requiredEnvVars.environment as string,
				trackResources: true,
				trackLongTasks: true,
				trackUserInteractions: true,
				trackSessionAcrossSubdomains: true,
				defaultPrivacyLevel: 'mask-user-input',
				sessionSampleRate: 100,
				sessionReplaySampleRate: 100,
				allowedTracingUrls: [/https:\/\/.*\.praxislabs\.co/, /https:\/\/.*\.torch\.io/],
				traceSampleRate: 100,
			});
		});
	}
};

export const setDatadogUser = async (user: { id: string; email: string; tenantId?: string; name?: string }) => {
	if (process.env.NEXT_PUBLIC_ENVIRONMENT) {
		const { datadogRum: rum } = await loadDatadogModules();
		rum.setUser({
			id: user.id,
			email: user.email,
			tenantId: user.tenantId,
			name: user.name,
		});
	}
};

export const CLIENT_EVENT = {
	SERVER_ACTION_SKEW: 'server_action_skew',
	SCORM_HOST_UNREACHABLE: 'scorm_host_unreachable',
	SCORM_HANDSHAKE_TIMEOUT: 'scorm_handshake_timeout',
} as const;

export type ClientEventName = (typeof CLIENT_EVENT)[keyof typeof CLIENT_EVENT];

export type DatadogLogLevel = 'info' | 'warn' | 'error';

const writeDatadogEvent = async (
	name: string,
	context: Record<string, unknown> | undefined,
	level: DatadogLogLevel,
): Promise<void> => {
	try {
		const { datadogRum: rum, datadogLogs: logs } = await loadDatadogModules();
		rum.addAction(name, context);

		if (level === 'error') {
			rum.addError(new Error(name), context);
			logs.logger.error(name, context);
			return;
		}

		if (level === 'warn') {
			logs.logger.warn(name, context);
			return;
		}

		logs.logger.info(name, context);
	} catch (error) {
		console.warn(`Failed to log client event to Datadog: ${name}`, error);
	}
};

export const logClientEvent = async (
	name: ClientEventName,
	context?: Record<string, unknown>,
	level: DatadogLogLevel = 'info',
): Promise<void> => {
	if (typeof window === 'undefined') return;
	if (!isInitialized()) {
		await initializeDatadog();
	}
	if (!isInitialized()) {
		console.warn('Dropping Datadog event; SDK not initialized', name);
		return;
	}

	await writeDatadogEvent(name, context, level);
};

export type HumeClientEventContext = {
	characterName?: string;
	sessionId?: string;
	readyState?: string;
	closeCode?: number | string;
	closeReason?: string;
	errorType?: string;
	retryAttempt?: number;
	[key: string]: unknown;
};

export const logHumeClientEvent = async (
	eventName: string,
	eventContext: HumeClientEventContext = {},
	level: DatadogLogLevel = 'info',
): Promise<void> => {
	if (typeof window === 'undefined') return;
	if (!isInitialized()) {
		console.warn('Dropping Hume Datadog event; SDK not initialized', eventName);
		return;
	}

	await writeDatadogEvent(eventName, { ...eventContext, event: eventName }, level);
};
